import { useState } from 'react';
import { Plane, Info } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

// Direct Scannable Vector QR Code Component (App Store on iOS & Play Store on Android)
const SampleQRCode = ({ platform = 'ios' }) => {
  const url =
    platform === 'ios'
      ? 'https://apps.apple.com/in/app/trrip-your-ontour-assistant/id6759519854'
      : 'https://play.google.com/store/apps/details?id=live.trrip.app&hl=en_IN&bookingId=bysid';

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={`Open in ${platform === 'ios' ? 'Apple App Store' : 'Google Play Store'}`}
      className="p-1.5 bg-white rounded-xl border border-teal-200/80 shadow-2xs flex items-center justify-center cursor-pointer hover:border-[#067978] transition-colors"
    >
      <QRCodeSVG
        value={url}
        size={64}
        bgColor="#ffffff"
        fgColor="#0f172a"
        level="M"
        includeMargin={false}
      />
    </a>
  );
};

const SampleVoucherCard = ({ effectiveLogo, companyName, onOpenPreview }) => {
  const [qrPlatform, setQrPlatform] = useState('ios');

  return (
    <div className="xl:col-span-8 flex flex-col space-y-3.5 sm:space-y-4">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-lg sm:text-xl md:text-2xl font-normal text-slate-800 tracking-tight leading-snug">
            Sample voucher
          </h2>
        </div>
        <p className="text-xs sm:text-sm text-slate-500 mt-1 font-normal leading-relaxed">
          This sample voucher demonstrates what your custom-branded vouchers and trip decks look like to your travellers.
        </p>
      </div>

      {/* Ticket Card with unified borders and classic perforated stub */}
      <div
        data-tour="sample"
        className="bg-white rounded-2xl sm:rounded-3xl border border-[#c2ecea] shadow-xs hover:shadow-md transition-all overflow-hidden flex flex-col md:flex-row relative"
      >
        {/* Left Main Ticket Body */}
        <div className="flex-1 bg-white p-3.5 sm:p-5 lg:p-6 flex flex-col justify-between space-y-3.5">
          <div>
            {/* Voucher Top Tag & Status */}
            <div className="flex flex-wrap items-center justify-between gap-1.5 sm:gap-2">
              <div className="flex items-center gap-2">
                <img
                  src={effectiveLogo}
                  alt={companyName}
                  className="h-7 sm:h-8 max-h-8 w-auto max-w-[110px] object-contain rounded"
                />
                <span className="text-[10px] sm:text-[11px] font-medium tracking-wider text-primary uppercase">
                  VOUCHER · ISSUED BY {companyName}
                </span>
              </div>
              <span className="text-[10px] sm:text-xs font-normal px-2.5 py-0.5 rounded-full bg-teal-50 text-[#067978] border border-teal-200/70 shrink-0">
                Upcoming · in 12 days
              </span>
            </div>

            {/* Cities Heading with Centered Themed Airplane Flight Path */}
            <div className="flex items-center justify-between gap-2 xs:gap-3 sm:gap-4 mt-2.5 sm:mt-4">
              <span className="text-lg xs:text-xl sm:text-2xl lg:text-3xl font-serif text-[#064242] tracking-tight font-normal shrink-0">
                Mumbai
              </span>

              {/* Themed Flight Path with Centered Airplane Badge */}
              <div className="flex-1 mx-1.5 xs:mx-2 sm:mx-5 flex items-center relative">
                <div className="w-full border-t-2 border-dashed border-teal-200/90"></div>
                <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-5 h-5 xs:w-6 xs:h-6 sm:w-7 sm:h-7 rounded-full bg-[#eaf7f5] border border-[#c2ecea] flex items-center justify-center shadow-2xs">
                  <Plane size={12} className="text-[#067978] rotate-90" />
                </div>
              </div>

              <span className="text-lg xs:text-xl sm:text-2xl lg:text-3xl font-serif text-[#064242] tracking-tight font-normal shrink-0">
                Jaipur
              </span>
            </div>

            {/* 6 Grid Specs */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3.5 mt-3 sm:mt-3.5 pt-3 border-t border-slate-100">
              <div>
                <span className="text-[9.5px] sm:text-[10px] uppercase font-medium text-slate-400 block tracking-wider">
                  TRAVELLER
                </span>
                <span className="text-xs sm:text-sm font-normal text-slate-800 block mt-0.5">
                  Karan Verma <span className="text-slate-500 text-xs">+1 adult</span>
                </span>
              </div>

              <div>
                <span className="text-[9.5px] sm:text-[10px] uppercase font-medium text-slate-400 block tracking-wider">
                  DATES
                </span>
                <span className="text-xs sm:text-sm font-normal text-slate-800 block mt-0.5">
                  5 – 8 Sep 2026 · 4 days
                </span>
              </div>

              <div>
                <span className="text-[9.5px] sm:text-[10px] uppercase font-medium text-slate-400 block tracking-wider">
                  HOTEL
                </span>
                <span className="text-xs sm:text-sm font-normal text-slate-800 block mt-0.5">
                  Hotel Pearl Palace
                </span>
              </div>

              <div>
                <span className="text-[9.5px] sm:text-[10px] uppercase font-medium text-slate-400 block tracking-wider">
                  TRAVEL
                </span>
                <span className="text-xs sm:text-sm font-normal text-slate-800 block mt-0.5">
                  IndiGo 6E-214 · 07:40
                </span>
              </div>

              <div>
                <span className="text-[9.5px] sm:text-[10px] uppercase font-medium text-slate-400 block tracking-wider">
                  ITINERARY
                </span>
                <span className="text-xs sm:text-sm font-normal text-slate-800 block mt-0.5">
                  7 stops · Amer, Hawa Mahal...
                </span>
              </div>

              <div>
                <span className="text-[9.5px] sm:text-[10px] uppercase font-medium text-slate-400 block tracking-wider">
                  CONTACTS
                </span>
                <span className="text-xs sm:text-sm font-normal text-slate-800 block mt-0.5">
                  Driver, hotel added
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-2.5 pt-3 border-t border-slate-100">
            <button
              type="button"
              data-tour="open-sample-btn"
              onClick={onOpenPreview}
              className="px-4 py-2 rounded-xl bg-primary hover:bg-[#056564] text-white text-xs font-normal transition-all shadow-xs active:scale-95 shrink-0 cursor-pointer"
            >
              Open sample voucher
            </button>
          </div>
        </div>

        {/* Right Perforated Stub ("TRAVELLER CODE") with Perforated Cutout Notches */}
        <div className="w-full md:w-48 bg-[#f2faf8] border-t-2 md:border-t-0 md:border-l-2 border-dashed border-[#b6e7e4] p-4 sm:p-5 flex flex-col items-center justify-center text-center relative">
          {/* Decorative Circular Ticket Notches */}
          <div className="hidden md:block absolute -top-2.5 -left-2.5 w-5 h-5 bg-white border border-[#c2ecea] rounded-full z-10"></div>
          <div className="hidden md:block absolute -bottom-2.5 -left-2.5 w-5 h-5 bg-white border border-[#c2ecea] rounded-full z-10"></div>

          <span className="text-[10px] font-semibold text-[#067978] tracking-widest uppercase block">
            TRAVELLER CODE
          </span>

          {/* 5-char Code Box */}
          <div className="mt-1.5 px-3 py-1 rounded-xl bg-white border border-teal-200 shadow-2xs text-sm sm:text-base font-mono font-medium text-slate-800 tracking-widest">
            bysid
          </div>

          <p className="text-[9.5px] text-slate-500 font-normal leading-tight mt-1 max-w-[120px]">
            Traveller enters this in your app to log in
          </p>

          {/* QR Code with iOS / Android Platform Switcher */}
          <div className="mt-2 flex flex-col items-center">
            {/* Platform Toggle */}
            <div className="inline-flex p-0.5 bg-teal-100/70 rounded-lg mb-1.5 border border-teal-200/60">
              <button
                type="button"
                onClick={() => setQrPlatform('ios')}
                className={`px-2 py-0.5 text-[9.5px] font-semibold rounded-md transition-all cursor-pointer ${
                  qrPlatform === 'ios'
                    ? 'bg-[#067978] text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                iOS
              </button>
              <button
                type="button"
                onClick={() => setQrPlatform('android')}
                className={`px-2 py-0.5 text-[9.5px] font-semibold rounded-md transition-all cursor-pointer ${
                  qrPlatform === 'android'
                    ? 'bg-[#067978] text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Android
              </button>
            </div>

            <SampleQRCode platform={qrPlatform} />

            <span className="text-[8.5px] text-slate-500 font-medium mt-1 block">
              Scan for {qrPlatform === 'ios' ? 'App Store' : 'Play Store'}
            </span>
          </div>
        </div>
      </div>

      {/* Footnote Message below ticket */}
      <div className="flex items-start gap-1.5 text-slate-500 text-[11px] sm:text-xs font-normal leading-relaxed pt-0.5">
        <Info size={14} className="text-primary shrink-0 mt-0.5" />
        <span>
          What your traveller sees: a 4-day interactive itinerary, real-time transit updates, emergency contacts, and curated local recommendations - 100% white-labelled to you.
        </span>
      </div>
    </div>
  );
};

export default SampleVoucherCard;
